import { useCallback, useEffect, useRef, useState } from 'react';
import {
  deletePassportIdleVideo,
  getPassportKioskScanStateForPartner,
  setPassportKioskScanState,
  uploadPassportIdleVideo,
} from '../../face-identity/utils/passportKioskApi';
import { PORTAL_ID } from '../utils/api';
import { modernConfirm, modernPrompt } from '../../admin-shared/modernDialog';

export function usePassportKioskPartnerControl() {
  const [scanEnabled, setScanEnabled] = useState(false);
  const [idleVideoUrl, setIdleVideoUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [videoBusy, setVideoBusy] = useState(false);
  const [error, setError] = useState('');
  const [videoNotice, setVideoNotice] = useState('');
  const fileRef = useRef(null);

  const applyState = useCallback((data) => {
    setScanEnabled(Boolean(data?.scan_enabled));
    setIdleVideoUrl(String(data?.idle_video_url || '').trim());
  }, []);

  const load = useCallback(async () => {
    try {
      const data = await getPassportKioskScanStateForPartner(PORTAL_ID);
      applyState(data);
      setError('');
    } catch (err) {
      setError(err?.message || 'Could not load Passport kiosk state');
    } finally {
      setLoading(false);
    }
  }, [applyState]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleScan = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const data = await setPassportKioskScanState(!scanEnabled, PORTAL_ID);
      applyState(data);
      setError('');
    } catch (err) {
      setError(err?.message || 'Could not update Passport kiosk');
    } finally {
      setBusy(false);
    }
  };

  const onPickVideo = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || videoBusy) return;
    setVideoBusy(true);
    setVideoNotice('');
    try {
      const data = await uploadPassportIdleVideo(file, PORTAL_ID);
      applyState(data);
      setVideoNotice('Idle video updated.');
      setError('');
    } catch (err) {
      setError(err?.message || 'Could not upload idle video');
    } finally {
      setVideoBusy(false);
    }
  };

  const onRemoveVideo = async () => {
    if (videoBusy || !idleVideoUrl) return;
    if (!(await modernConfirm('Remove the Passport idle video?'))) return;
    setVideoBusy(true);
    setVideoNotice('');
    try {
      const data = await deletePassportIdleVideo(PORTAL_ID);
      applyState(data);
      setVideoNotice('Idle video removed.');
      setError('');
    } catch (err) {
      setError(err?.message || 'Could not remove idle video');
    } finally {
      setVideoBusy(false);
    }
  };

  return {
    scanEnabled,
    idleVideoUrl,
    loading,
    busy,
    videoBusy,
    error,
    videoNotice,
    fileRef,
    toggleScan,
    onPickVideo,
    onRemoveVideo,
    openFilePicker: () => fileRef.current?.click(),
  };
}
